<template>
  <ValidationObserver ref="form">
    <!-- Standard Login Form -->
    <form v-if="!emailSent && !show2FASetup && !showMFAInput" @submit.prevent="submit">
      <z-input v-model="username" type="text" name="username" label="Username" :validator="v$.username" />
      <z-input v-model="password" type="password" name="password" label="Password" :validator="v$.password" />

      <!-- 2FA Setup Option -->
      <div class="form-check mb-3">
        <input v-model="setup2FA" class="form-check-input" type="checkbox" id="setup2FA">
        <label class="form-check-label" for="setup2FA">
          Set up Two-Factor Authentication
        </label>
      </div>

      <div class="custom-button">
        <button type="submit" class="btn btn-primary btn-block" :disabled="isLoading">
          <div class="custom-button-text">
            <span>Log In</span>
            <div v-show="isLoading" class="ms-2">
              <FontAwesomeIcon v-if="isLoading" icon="fa-solid fa-spinner me-2" spin />
            </div>
          </div>
        </button>
      </div>
      <div class="custom-button">
        <button v-if="isFailedLogin" type="submit" class="btn btn-primary btn-block mt-2" :disabled="isLoading"
          @click="passwordReset">
          Forgot my Password
        </button>
      </div>
      <z-message v-model="message" class="alert alert-warning mt-2" />
    </form>

    <!-- 2FA Setup Process -->
    <div v-if="show2FASetup" class="twofa-setup">
      <h5>Set Up Two-Factor Authentication</h5>

      <!-- Step 1: Show QR Code -->
      <div v-if="currentStep === 'qr'" class="text-center">
        <p>Scan this QR code with your authenticator app:</p>
        <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="2FA QR Code" class="qr-code mb-3" />
        <p class="small text-muted">Or enter this code manually: {{ manualEntryKey }}</p>

        <button class="btn btn-primary" @click="currentStep = 'verify'">
          I've Added the Account
        </button>
      </div>

      <!-- Step 2: Verify Setup -->
      <div v-if="currentStep === 'verify'">
        <p>Enter the 6-digit code from your authenticator app:</p>
        <z-input v-model="verificationCode" type="text" name="verificationCode" label="Verification Code" maxlength="6"
          placeholder="000000" />
        <div class="d-flex gap-2">
          <button class="btn btn-success" :disabled="isLoading || verificationCode.length !== 6"
            @click="performConfirm2FA">
            <FontAwesomeIcon v-if="isLoading" icon="fa-solid fa-spinner fa-spin me-2" />
            Confirm Setup
          </button>
          <button class="btn btn-secondary" @click="cancel2FASetup">
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- MFA Input for Login -->
    <div v-if="showMFAInput">
      <h5>Two-Factor Authentication</h5>
      <p>Enter the 6-digit code from your authenticator app:</p>
      <z-input v-model="mfaCode" type="text" name="mfaCode" label="Authentication Code" maxlength="6"
        placeholder="000000" />
      <div class="d-flex gap-2">
        <button class="btn btn-primary" :disabled="isLoading || mfaCode.length !== 6" @click="verifyMFA">
          <FontAwesomeIcon v-if="isLoading" icon="fa-solid fa-spinner fa-spin me-2" />
          Verify
        </button>
        <button class="btn btn-secondary" @click="cancelMFA">
          Back to Login
        </button>
      </div>
    </div>

    <!-- Password Reset Success -->
    <div v-if="emailSent" class="row">
      <div class="col">
        <div class="row">
          <div class="col">
            <p>
              No problem, a "Reset Password" link has been sent to your
              registered email address.
            </p>
            <p>
              Please look for that email and click the link to reset your
              password. You may need to check your spam folder.
            </p>
          </div>
        </div>
      </div>
    </div>
  </ValidationObserver>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { authStore } from '@/stores/auth'
import ZInput from '@/components/utilities/elements/Input.vue'
import ZMessage from '@/components/utilities/elements/Message.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'FormLogin',
  components: {
    ZInput,
    ZMessage,
    FontAwesomeIcon
  },
  data() {
    return {
      message: '',
      rules: {
        username: { required },
        password: { required },
      },
      v$: null,
      isLoading: false,
      isFailedLogin: false,
      emailSent: false,
      setup2FA: false,
      show2FASetup: false,
      showMFAInput: false,
      currentStep: 'qr', // 'qr' or 'verify'
      qrCodeUrl: null,
      manualEntryKey: '',
      verificationCode: '',
      mfaCode: '',
      otpauthUri: '',
      tempLoginData: null // Store login data for MFA verification
    }
  },
  beforeMount() {
    this.v$ = useVuelidate(this.rules, this.profile)
    this.isLoading = false
  },
  computed: {
    ...mapState(authStore, ['profile', 'token', 'passthrough']),
    isAdmin() {
      let _is = false
      if (this.profile?.roles?.[0] && [1, 2, 3, 5].includes(this.profile.roles[0])) {
        _is = true
      }
      return _is
    },
    username: {
      get() {
        return this.profile?.username || ''
      },
      set(value) {
        this.setData({
          name: 'username',
          value: value,
        })
      },
    },
    password: {
      get() {
        return this.profile?.password || ''
      },
      set(value) {
        this.setData({
          name: 'password',
          value: value,
        })
      },
    },
  },
  methods: {
    ...mapActions(authStore, [
      'login',
      'setData',
      'resetPassword',
      'initiate2FA',
      'confirm2FA',
      'verify2FA',
      'generate2FAQRCode'
    ]),

    loginAgain() {
      this.isLoading = false
      this.isFailedLogin = false
      this.emailSent = false
      this.showMFAInput = false
      this.show2FASetup = false
    },

    async passwordReset() {
      this.emailSent = false
      if (this.username.trim() !== '') {
        this.emailSent = true
        let result = await this.resetPassword({
          username: this.username.trim(),
        })
        if (result?.success) {
          // Password reset email sent successfully
        } else {
          this.emailSent = false
          this.isLoading = false
          this.message = ''
          this.isFailedLogin = false
          alert('Failed to generate a password reset, please contact support.')
        }
      } else {
        this.emailSent = false
        this.isLoading = false
        this.message = 'Please supply your username/email.'
      }
    },

    async submit() {
      try {
        this.message = ''
        const valid = await this.v$.$validate()

        if (!valid) {
          console.log('Validation failed')
          return
        }

        this.isLoading = true

        // If setting up 2FA, initiate the process
        if (this.setup2FA) {
          await this.initiate2FASetup()
          return
        }

        // Regular login attempt
        const loginResult = await this.login(this.username, this.password)

        if (loginResult?.requiresMfa) {
          // User has 2FA enabled, show MFA input
          this.tempLoginData = loginResult
          this.showMFAInput = true
          this.isLoading = false
        } else if (this.token) {
          // Successful login without 2FA
          console.log('logged in...')
          this.isLoading = false
          let page = '/'
          this.$router.push(page)
        } else {
          throw new Error('Login failed')
        }

      } catch (e) {
        this.isLoading = false
        this.isFailedLogin = true
        console.error(e)
        this.message = 'Error: Unable to login'
      }
    },

    async initiate2FASetup() {
      try {
        // First, perform regular login
        await this.login(this.username, this.password)

        if (!this.token) {
          throw new Error('Login required for 2FA setup')
        }

        // Initiate 2FA setup
        const initiateResult = await this.initiate2FA()

        if (initiateResult?.otpauth_uri) {
          this.otpauthUri = initiateResult.otpauth_uri
          this.manualEntryKey = initiateResult.manualEntryKey || ''

          // Generate QR code
          await this.generateQRCode()

          this.show2FASetup = true
          this.currentStep = 'qr'
        } else {
          throw new Error('Failed to initiate 2FA setup')
        }

        this.isLoading = false

      } catch (e) {
        this.isLoading = false
        this.isFailedLogin = true
        console.error(e)
        this.message = 'Error: Failed to initiate 2FA setup'
      }
    },

    async generateQRCode() {
      try {
        const qrResult = await this.generate2FAQRCode(this.otpauthUri)
        if (qrResult) {
          // Assuming the API returns a blob or base64 image
          this.qrCodeUrl = qrResult
        }
      } catch (e) {
        console.error('Failed to generate QR code:', e)
        this.message = 'Failed to generate QR code'
      }
    },

    async performConfirm2FA() {
      try {
        this.isLoading = true

        const confirmResult = await this.confirm2FA({
          code: this.verificationCode
        })

        if (confirmResult?.enabled) {
          this.message = '2FA setup completed successfully!'
          this.cancel2FASetup()
          // Reset to normal login
          this.setup2FA = false
        } else {
          throw new Error('Invalid verification code')
        }

        this.isLoading = false

      } catch (e) {
        this.isLoading = false
        console.error(e)
        this.message = 'Error: Invalid verification code'
      }
    },

    async verifyMFA() {
      try {
        this.isLoading = true

        const verifyResult = await this.verify2FA({
          code: this.mfaCode,
          loginData: this.tempLoginData
        })

        if (verifyResult?.token) {
          console.log('2FA verification successful')
          this.isLoading = false
          let page = '/'
          this.$router.push(page)
        } else {
          throw new Error('Invalid MFA code')
        }

      } catch (e) {
        this.isLoading = false
        console.error(e)
        this.message = 'Error: Invalid authentication code'
      }
    },

    cancel2FASetup() {
      this.show2FASetup = false
      this.currentStep = 'qr'
      this.qrCodeUrl = null
      this.manualEntryKey = ''
      this.verificationCode = ''
      this.otpauthUri = ''
      this.setup2FA = false
      this.message = ''
      this.isLoading = false
    },

    cancelMFA() {
      this.showMFAInput = false
      this.mfaCode = ''
      this.tempLoginData = null
      this.message = ''
      this.isLoading = false
    }
  },
}
</script>

<style lang="scss" scoped>
.custom-button {
  width: 100%;
  text-align: center;
}

.custom-button-text {
  display: inline-flex;
}

.twofa-setup {
  text-align: center;
}

.qr-code {
  max-width: 200px;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px;
  background: white;
}
</style>
